import { useEffect, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { api } from "@/lib/api";

const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// FIPS codes to USPS state abbreviations
const FIPS_TO_USPS = {
  "01":"AL","02":"AK","04":"AZ","05":"AR","06":"CA","08":"CO","09":"CT","10":"DE","11":"DC",
  "12":"FL","13":"GA","15":"HI","16":"ID","17":"IL","18":"IN","19":"IA","20":"KS","21":"KY",
  "22":"LA","23":"ME","24":"MD","25":"MA","26":"MI","27":"MN","28":"MS","29":"MO","30":"MT",
  "31":"NE","32":"NV","33":"NH","34":"NJ","35":"NM","36":"NY","37":"NC","38":"ND","39":"OH",
  "40":"OK","41":"OR","42":"PA","44":"RI","45":"SC","46":"SD","47":"TN","48":"TX","49":"UT",
  "50":"VT","51":"VA","53":"WA","54":"WV","55":"WI","56":"WY",
};

export default function USMap() {
  const [counts, setCounts] = useState({}); // { CA: 3, TN: 1, ... }
  const [hover, setHover] = useState(null);

  useEffect(() => {
    api.get("/sobriety/states").then(({ data }) => setCounts(data || {})).catch(() => {});
  }, []);

  const max = Math.max(1, ...Object.values(counts));

  const fillFor = (count) => {
    if (!count) return "#EFE9DA";
    // terracotta gradient
    const intensity = 0.35 + 0.55 * (count / max);
    return `rgba(224, 122, 95, ${intensity.toFixed(2)})`;
  };

  return (
    <div data-testid="us-map" className="relative">
      <ComposableMap projection="geoAlbersUsa" width={980} height={560} style={{ width: "100%", height: "auto" }}>
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const fips = String(geo.id).padStart(2, "0");
              const code = FIPS_TO_USPS[fips];
              const count = counts[code] || 0;
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={fillFor(count)}
                  stroke="#FDFBF7"
                  strokeWidth={0.8}
                  onMouseEnter={() => setHover({ name: geo.properties.name, count })}
                  onMouseLeave={() => setHover(null)}
                  style={{
                    default: { outline: "none" },
                    hover: { fill: "var(--terracotta-dark)", outline: "none", cursor: "pointer" },
                    pressed: { outline: "none" },
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>

      {hover && (
        <div className="absolute top-3 right-3 bg-[var(--ink)] text-[var(--bg)] px-4 py-2 rounded-sm text-sm shadow-lg pointer-events-none">
          <div className="font-medium">{hover.name}</div>
          <div className="text-xs text-white/70">{hover.count} {hover.count === 1 ? "pledge" : "pledges"}</div>
        </div>
      )}

      <div className="flex items-center gap-3 mt-6 text-xs text-[var(--ink-soft)]">
        <span>Fewer</span>
        <div className="flex">
          {[0.35, 0.5, 0.65, 0.8, 0.95].map((o) => (
            <span key={o} className="w-7 h-3" style={{ background: `rgba(224, 122, 95, ${o})` }} />
          ))}
        </div>
        <span>More</span>
        <span className="ml-auto font-mono-acc">{Object.keys(counts).length}/50 states represented</span>
      </div>
    </div>
  );
}
